import { Check, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { formatAppDateTime } from '@/lib/locale'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import { Badge } from '@/components/ui/badge'
import type { AppNotificationRecord } from '@/types/notifications'
import { getNotificationSemantic } from '../lib/notification-semantic'
import { resolveNotificationActionUrl } from '../lib/notification-router'
import { getLocalizedNotification } from '../lib/notification-content'
import { formatCompactRelativeTime } from '../lib/relative-time'
import { NotificationActionButtons } from './NotificationActionButtons'

interface NotificationCardProps {
  item: AppNotificationRecord
  onMarkRead?: () => void
  isBusy?: boolean
  isArchive?: boolean
}

export function NotificationCard({
  item,
  onMarkRead,
  isBusy,
  isArchive = false,
}: NotificationCardProps) {
  const { t } = useTranslation()
  const semantic = getNotificationSemantic(item)
  const actionUrl = resolveNotificationActionUrl(item)
  const { title, message } = getLocalizedNotification(item, t)

  const Icon = semantic.icon

  const titleContent = (
    <div className="flex flex-wrap items-center gap-2">
      <ItemTitle className="text-base font-semibold tracking-tight line-clamp-1">
        {title}
      </ItemTitle>
      <Badge
        variant="outline"
        className={cn(
          'shrink-0 text-[10px] uppercase font-semibold tracking-wider rounded-md py-0 h-5',
          semantic.badgeClass,
        )}
      >
        {t(semantic.badgeLabelKey)}
      </Badge>
    </div>
  )

  return (
    <Item
      variant="outline"
      className={cn(
        'relative flex-col sm:flex-row items-start sm:items-center p-4',
        'rounded-xl border bg-card',
        isArchive && 'opacity-70',
      )}
    >
      {/* Unread indicator strip */}
      {!isArchive && (
        <div className="absolute top-0 bottom-0 left-0 w-1 bg-primary rounded-l-xl" />
      )}

      {/* Icon */}
      <ItemMedia
        variant="icon"
        className={cn('size-10 sm:size-12 rounded-xl', semantic.mediaClass)}
      >
        <Icon className={cn('size-5', semantic.iconClass)} />
      </ItemMedia>

      {/* Content */}
      <ItemContent className="flex-1 min-w-0 mt-2 sm:mt-0 px-1 sm:px-2 space-y-1">
        {titleContent}
        <ItemDescription className="text-sm leading-relaxed md:pr-10">
          {message}
        </ItemDescription>
      </ItemContent>

      {/* Actions */}
      <ItemActions className="mt-4 w-full flex flex-row items-center justify-between sm:w-auto sm:mt-0 sm:flex-col sm:items-end gap-3 self-end sm:self-stretch">
        <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1.5 bg-muted/50 px-2 py-1 rounded-md mb-auto">
          <Clock className="size-3" />
          {formatAppDateTime(item.created_at)}
        </span>

        <NotificationActionButtons
          actionUrl={actionUrl}
          onMarkRead={onMarkRead}
          isBusy={isBusy}
          isArchive={isArchive}
        />
      </ItemActions>
    </Item>
  )
}

/* Compact variant for the header dropdown */
interface NotificationDropdownItemProps {
  item: AppNotificationRecord
  onMarkRead?: () => void
}

export function NotificationDropdownItem({ item, onMarkRead }: NotificationDropdownItemProps) {
  const { t } = useTranslation()
  const semantic = getNotificationSemantic(item)
  const actionUrl = resolveNotificationActionUrl(item)
  const { title, message } = getLocalizedNotification(item, t)
  const Icon = semantic.icon

  const wrapperClassName = cn(
    'flex items-start gap-2.5 px-3 py-2.5',
    !item.read_at && 'bg-primary/[0.02]',
  )

  if (actionUrl) {
    return (
      <Link to={actionUrl} className={wrapperClassName}>
      <div
        className={cn(
          'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full',
          semantic.mediaClass,
        )}
      >
        <Icon className={cn('size-4', semantic.iconClass)} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm leading-tight', !item.read_at && 'font-semibold')}>
          {title}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
          {message}
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground/70">
          {formatCompactRelativeTime(item.created_at)}
        </p>
      </div>
      {!item.read_at && onMarkRead ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onMarkRead()
          }}
          className="mt-1.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          title="Mark as read"
        >
          <Check className="size-3.5" />
        </button>
      ) : !item.read_at ? (
        <div className="mt-2 size-2 rounded-full bg-primary shrink-0" />
      ) : null}
      </Link>
    )
  }

  return (
    <div className={wrapperClassName}>
      <div
        className={cn(
          'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full',
          semantic.mediaClass,
        )}
      >
        <Icon className={cn('size-4', semantic.iconClass)} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm leading-tight', !item.read_at && 'font-semibold')}>
          {title}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
          {message}
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground/70">
          {formatCompactRelativeTime(item.created_at)}
        </p>
      </div>
      {!item.read_at && onMarkRead ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onMarkRead()
          }}
          className="mt-1.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          title="Mark as read"
        >
          <Check className="size-3.5" />
        </button>
      ) : !item.read_at ? (
        <div className="mt-2 size-2 rounded-full bg-primary shrink-0" />
      ) : null}
    </div>
  )
}


